import { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { StepNavigation } from "../components/ui/StepNavigation";
import GeneralInfo from "../components/bessOnboarding/GeneralInfo";
import AssetSpec from "../components/bessOnboarding/AssetSpec";
import ContractPar from "../components/bessOnboarding/ContractPar";
import ContractProp from "../components/bessOnboarding/ContractProp";
import { toastConfig } from "../components/config/toast.config";
import { RootState } from "../store/indexRedux";
import { resetStep1, resetApiData, clearAllBessOnboardingCache, BESS_STORAGE_KEYS } from "../store/bessOnboarding";
import "react-toastify/dist/ReactToastify.css";

function BessOnboarding() {
  const dispatch = useDispatch();
  const selectedCompany = useSelector((state: RootState) => state.selectedCompany.selectedUserCompany);

  const getCurrentCompanyId = () => {
    return (
      selectedCompany?.company_id?.toString() ||
      JSON.parse(localStorage.getItem("selectedUserCompany") || "{}")?.company_id?.toString() ||
      null
    );
  };

  const checkCompanyChanged = () => {
    const currentCompanyId = getCurrentCompanyId();
    if (!currentCompanyId) return false;
    const savedCompanyId = localStorage.getItem(BESS_STORAGE_KEYS.companyId);
    return savedCompanyId && savedCompanyId !== currentCompanyId;
  };

  const resetWizard = useCallback(() => {
    dispatch(resetStep1());
    dispatch(resetApiData());
    clearAllBessOnboardingCache();
    setCurrentStep(1);
    setHighestStepReached(1);
  }, [dispatch]);

  const [currentStep, setCurrentStep] = useState<number>(() => {
    if (checkCompanyChanged()) return 1;
    try {
      const cached = JSON.parse(localStorage.getItem(BESS_STORAGE_KEYS.wizard) || "{}");
      const cs = Number(cached?.currentStep);
      return Number.isInteger(cs) && cs >= 1 && cs <= 4 ? cs : 1;
    } catch {
      return 1;
    }
  });

  const [bessOnboardingHardStop, setBessOnboardingHardStop] = useState(false);

  const [highestStepReached, setHighestStepReached] = useState<number>(() => {
    if (checkCompanyChanged()) return 1;
    try {
      const cached = JSON.parse(localStorage.getItem(BESS_STORAGE_KEYS.wizard) || "{}");
      const hr = Number(cached?.highestStepReached);
      return Number.isInteger(hr) && hr >= 1 && hr <= 4 ? hr : 1;
    } catch {
      return 1;
    }
  });

  useEffect(() => {
    const currentCompanyId =
      selectedCompany?.company_id?.toString() ||
      JSON.parse(localStorage.getItem("selectedUserCompany") || "{}")?.company_id?.toString() ||
      null;

    if (currentCompanyId) {
      const savedCompanyId = localStorage.getItem(BESS_STORAGE_KEYS.companyId);

      if (savedCompanyId && savedCompanyId !== currentCompanyId) {
        resetWizard();
      }

      localStorage.setItem(BESS_STORAGE_KEYS.companyId, currentCompanyId);
    } else {
      resetWizard();
    }
  }, [selectedCompany?.company_id, dispatch, resetWizard]);

  useEffect(() => {
    localStorage.setItem(BESS_STORAGE_KEYS.wizard, JSON.stringify({ currentStep, highestStepReached }));
  }, [currentStep, highestStepReached]);

  const handleStepClick = (stepNumber: number) => {
    if (stepNumber <= highestStepReached) {
      setCurrentStep(stepNumber);
    }
  };

  const handleNext = () => {
    if (currentStep < 4) {
      const nextStep = currentStep + 1;
      setCurrentStep(nextStep);
      if (nextStep > highestStepReached) {
        setHighestStepReached(nextStep);
      }
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleResetProgress = useCallback(() => {
    setHighestStepReached(1);
  }, []);

  const handleSubmit = () => {
    resetWizard();
  };

  const steps = [
    { number: 1, label: "General information" },
    { number: 2, label: "Asset specifications" },
    { number: 3, label: "Contract parameters" },
    { number: 4, label: "Contract proposal" },
  ];

  return (
    <div className="mb-24">
      <ToastContainer {...toastConfig} />
      <div className="mt-20">
        <StepNavigation
          steps={steps}
          currentStep={currentStep}
          highestStepReached={bessOnboardingHardStop ? 1 : highestStepReached}
          onStepClick={handleStepClick}
        />
      </div>

      <div className="lg:w-[80%] p-8 mt-[6rem] mx-auto">
        <div className="bg-gray-50 dark:bg-transparent rounded-lg">
          {currentStep === 1 && <GeneralInfo onNext={handleNext} onPrevious={handlePrevious} onResetProgress={handleResetProgress} onHardStop={setBessOnboardingHardStop} />}
          {currentStep === 2 && <AssetSpec onNext={handleNext} onPrevious={handlePrevious} />}
          {currentStep === 3 && <ContractPar onNext={handleNext} onPrevious={handlePrevious} />}
          {currentStep === 4 && <ContractProp onSubmit={handleSubmit} onPrevious={handlePrevious} />}
        </div>
      </div>
    </div>
  );
}

export default BessOnboarding;
